/**
 * Plain-language explainers shown at the top of each Program News tab, so an
 * operator who has never seen the pipeline can read what a tab shows and what
 * the controls do. Keep each paragraph to one idea; no jargon without a gloss.
 */
export type PanelIntro = {
  title: string;
  paragraphs: string[];
};

export const PANEL_INTROS: Record<"sources" | "articles" | "events" | "runs", PanelIntro> = {
  sources: {
    title: "What this tab shows",
    paragraphs: [
      "One row per golf program: the official news feed we found on the school's athletics site, whether it verified, and what happened the last time we read it. Programs on an athlete's list come first.",
    ],
  },
  articles: {
    title: "What this tab shows",
    paragraphs: [
      "Every article the collector filed from a verified feed. Only extracted text is kept, never the page itself.",
      "New means waiting for classification. Suppressed means thrown out by the rules before any model call (round-by-round updates, previews, honor rolls). Classified means the model sorted it. Failed means the model call errored and a person should look.",
      "Every row now shows why it got its label: the rule that suppressed it, or the model's reason. Older rows say the reason was not recorded. Hover any underlined term for its meaning.",
      "Click any row to read the source and record whether the call was right.",
    ],
  },
  events: {
    title: "What this tab shows",
    paragraphs: [
      "Events the sorter produced from the articles, with duplicate coverage merged into one. Nothing here is sent to anyone yet.",
      "Alerts are time-sensitive changes (a coach change, a signing, a national title) and are only kept when the claim is verified against the article body. Digest items are useful weekly context such as schedules, postseason bids, and major results. Ignore is what the model read and passed on, including alert-shaped headlines the body did not back up.",
      'Alerts are rare on purpose: the article text must confirm the claim, otherwise it drops to Digest or Ignore. Every event shows why under "Why:"; older ones say the reason was not recorded.',
      "Click any row to read the source and record whether the call was right.",
    ],
  },
  runs: {
    title: "How the pipeline runs",
    paragraphs: [
      "Every night the pipeline runs two steps where its flags are on. Collection visits each verified news feed, files any new articles, and notes feeds that went quiet or failed. Classification reads the new articles, drops routine noise with rules, and asks the model to sort the rest into alert, digest, or ignore.",
      "The buttons start a step right away, in this environment only, over the number of sources or articles you enter; keep manual runs small. The full-run buttons work through everything, which is what the nightly job does; the size boxes are for quick tests. The run appears at the top of the table as soon as it starts and the row updates while it runs, so you can leave and come back.",
      "The cost line is what the latest classification spent on model calls, projected to a month of nightly runs. Above $25 a month we get a Slack warning; at $50 the run stops itself.",
    ],
  },
};
